/**
 * CBAMScenarioWidget — Phase 7.2 (Intelligence Engine).
 *
 * Interactive "what if?" simulator for CBAM exposure. Users pick a product,
 * the widget auto-loads the latest exposure as the baseline, and they can
 * dial in three modifiers:
 *   • Emission reduction (%)         — retrofits / process efficiency
 *   • New supplier intensity (tCO₂e/t) — switch to a cleaner supplier
 *   • Volume change (%)              — market swings, growth, phase-out
 *
 * Backend: /api/cbam/scenarios/simulate (compute) and /api/cbam/scenarios (persist).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  ArrowRight, LineChart, Loader2, Save, TrendingDown, TrendingUp, History, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

function fmtEur(v) {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1_000_000) return `€${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)     return `€${(n / 1_000).toFixed(1)}k`;
  return `€${n.toFixed(0)}`;
}

export default function CBAMScenarioWidget({ d }) {
  const products = d?.products || [];
  const [productId, setProductId] = useState('');
  const [baseline, setBaseline] = useState(null);        // latest exposure for the picked product
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [modifiers, setModifiers] = useState({
    emission_reduction_pct: '',
    supplier_intensity: '',
    import_volume_change: '',
  });
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDesc, setSaveDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [savedList, setSavedList] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);

  // Auto-select first product
  useEffect(() => {
    if (!productId && products.length) setProductId(products[0].id);
  }, [products, productId]);

  // Fetch baseline whenever product changes
  const loadBaseline = useCallback(async (pid) => {
    if (!pid) { setBaseline(null); return; }
    setBaselineLoading(true);
    setResult(null);
    try {
      const r = await api.get(`/cbam/exposures/${pid}`);
      setBaseline(r.data);
    } catch (err) {
      setBaseline(null); // 404 → no baseline; user must compute one first via the exposure widget
    } finally {
      setBaselineLoading(false);
    }
  }, []);
  useEffect(() => { loadBaseline(productId); }, [productId, loadBaseline]);

  const productName = useMemo(() => {
    const p = products.find((x) => x.id === productId);
    return p?.name || baseline?.product_name || 'Product';
  }, [products, productId, baseline]);

  const handleSimulate = async () => {
    if (!productId) { toast.error('Select a product first'); return; }
    if (!baseline) {
      toast.error('Run a CBAM exposure calculation first (in the Exposure widget).');
      return;
    }
    const anyMod = ['emission_reduction_pct', 'supplier_intensity', 'import_volume_change']
      .some((k) => modifiers[k] !== '' && Number(modifiers[k]) !== 0);
    if (!anyMod) { toast.error('Set at least one modifier to simulate.'); return; }

    setRunning(true);
    try {
      const r = await api.post('/cbam/scenarios/simulate', {
        product_id: productId,
        baseline: {
          industry: baseline.industry,
          country_of_origin: baseline.country_of_origin,
          import_volume: baseline.import_volume,
          carbon_intensity: baseline.carbon_intensity,
        },
        modifiers: {
          emission_reduction_pct: parseFloat(modifiers.emission_reduction_pct) || 0,
          supplier_intensity: parseFloat(modifiers.supplier_intensity) || 0,
          import_volume_change: parseFloat(modifiers.import_volume_change) || 0,
        },
      });
      setResult(r.data);
      toast.success(`Scenario computed · ${r.data.impact.text}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Simulation failed');
    } finally { setRunning(false); }
  };

  const handleSave = async () => {
    if (!result || !saveName.trim()) { toast.error('Give the scenario a name'); return; }
    setSaving(true);
    try {
      await api.post('/cbam/scenarios', {
        product_id: productId,
        product_name: productName,
        name: saveName.trim(),
        description: saveDesc.trim(),
        baseline: result.baseline,
        scenario: result.scenario,
        result,
      });
      toast.success('Scenario saved');
      setSaveOpen(false);
      setSaveName(''); setSaveDesc('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const openSaved = async () => {
    setSavedOpen(true);
    setSavedLoading(true);
    try {
      const r = await api.get('/cbam/scenarios', { params: { limit: 25 } });
      setSavedList(r.data?.scenarios || []);
    } catch (err) {
      setSavedList([]);
    } finally { setSavedLoading(false); }
  };

  const deleteSaved = async (id) => {
    try {
      await api.delete(`/cbam/scenarios/${id}`);
      setSavedList((xs) => xs.filter((x) => x.id !== id));
      toast.success('Scenario removed');
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const impact = result?.impact;
  const savingPositive = (impact?.saving || 0) > 0;

  return (
    <Card className="p-5" data-testid="wgt-cbam-scenario">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center">
            <LineChart className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <div className="font-heading font-semibold text-sm">CBAM Scenario Simulator</div>
            <div className="text-[11px] text-muted-foreground">What-if modelling · supplier · efficiency · volume</div>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={openSaved} data-testid="cbam-scenario-history-btn">
          <History className="w-3.5 h-3.5 mr-1" /> Saved
        </Button>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-xs">Product</Label>
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger data-testid="cbam-scenario-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
            <SelectContent>
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              {!products.length && <SelectItem value="__none__" disabled>No products yet</SelectItem>}
            </SelectContent>
          </Select>
        </div>

        {baselineLoading ? (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading baseline…
          </div>
        ) : baseline ? (
          <div className="text-[11px] text-muted-foreground border rounded-md p-2 bg-muted/40" data-testid="cbam-scenario-baseline">
            Baseline · {baseline.industry} · {baseline.country_of_origin} ·
            {' '}{Number(baseline.import_volume).toLocaleString()}t/yr ·
            {' '}{Number(baseline.carbon_intensity).toFixed(2)} tCO₂e/t ·
            <span className="font-mono ml-1">{fmtEur(baseline.estimated_cbam_cost)}</span>
          </div>
        ) : (
          <div className="text-[11px] text-amber-700 dark:text-amber-400 border border-amber-500/30 bg-amber-500/5 rounded-md p-2">
            No baseline yet — calculate CBAM exposure for this product first (Exposure widget above).
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Emission reduction</Label>
            <Input
              type="number" step="0.1" placeholder="% ↓"
              value={modifiers.emission_reduction_pct}
              onChange={(e) => setModifiers({ ...modifiers, emission_reduction_pct: e.target.value })}
              data-testid="cbam-scenario-reduction-input"
            />
          </div>
          <div>
            <Label className="text-xs">New supplier</Label>
            <Input
              type="number" step="0.01" placeholder="tCO₂e/t"
              value={modifiers.supplier_intensity}
              onChange={(e) => setModifiers({ ...modifiers, supplier_intensity: e.target.value })}
              data-testid="cbam-scenario-supplier-input"
            />
          </div>
          <div>
            <Label className="text-xs">Volume change</Label>
            <Input
              type="number" step="0.1" placeholder="% Δ"
              value={modifiers.import_volume_change}
              onChange={(e) => setModifiers({ ...modifiers, import_volume_change: e.target.value })}
              data-testid="cbam-scenario-volume-input"
            />
          </div>
        </div>

        <Button
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
          disabled={running || !baseline}
          onClick={handleSimulate}
          data-testid="cbam-scenario-run-btn"
        >
          {running
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Simulating…</>
            : <>Run simulation</>}
        </Button>

        {result && (
          <div className="border rounded-lg p-3 space-y-3" data-testid="cbam-scenario-result">
            <div className="grid grid-cols-3 gap-2 items-center">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Baseline</div>
                <div className="text-lg font-heading font-bold" data-testid="cbam-scenario-baseline-cost">
                  {fmtEur(result.baseline.estimated_cost)}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {Number(result.baseline.carbon_intensity).toFixed(2)} · {Number(result.baseline.import_volume).toLocaleString()}t
                </div>
              </div>
              <div className="flex justify-center">
                <ArrowRight className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Scenario</div>
                <div className={`text-lg font-heading font-bold ${savingPositive ? 'text-emerald-600' : 'text-red-600'}`}
                     data-testid="cbam-scenario-scenario-cost">
                  {fmtEur(result.scenario.estimated_cost)}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {Number(result.scenario.carbon_intensity).toFixed(2)} · {Number(result.scenario.import_volume).toLocaleString()}t
                </div>
              </div>
            </div>

            <div className={`rounded-md px-3 py-2 text-sm flex items-start gap-2 ${
              impact.type === 'positive' ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
              : impact.type === 'negative' ? 'bg-red-500/10 text-red-700 dark:text-red-300'
              : 'bg-muted text-muted-foreground'
            }`} data-testid="cbam-scenario-impact">
              {savingPositive ? <TrendingDown className="w-4 h-4 mt-0.5 shrink-0" /> : <TrendingUp className="w-4 h-4 mt-0.5 shrink-0" />}
              <div>
                <div className="font-medium">{impact.text}</div>
                <div className="text-[11px]">
                  {impact.percentage_change > 0 ? '+' : ''}{impact.percentage_change.toFixed(1)}% vs baseline
                </div>
              </div>
            </div>

            {result.modifiers_applied?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {result.modifiers_applied.map((m) => (
                  <Badge key={m} variant="outline" className="text-[10px]">{m}</Badge>
                ))}
              </div>
            )}

            <Button
              variant="outline" size="sm" className="w-full"
              onClick={() => setSaveOpen(true)}
              data-testid="cbam-scenario-save-btn"
            >
              <Save className="w-3.5 h-3.5 mr-1" /> Save scenario
            </Button>
          </div>
        )}
      </div>

      {/* Save Dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="cbam-scenario-save-dialog">
          <DialogHeader>
            <DialogTitle>Save scenario</DialogTitle>
            <DialogDescription>
              Persist this comparison to your organisation so it can be shared with your CFO or auditor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={saveName} onChange={(e) => setSaveName(e.target.value)}
                     placeholder="e.g. Switch to Turkish supplier"
                     data-testid="cbam-scenario-save-name" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={saveDesc} onChange={(e) => setSaveDesc(e.target.value)}
                     placeholder="Assumptions or context"
                     data-testid="cbam-scenario-save-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !saveName.trim()} data-testid="cbam-scenario-save-confirm">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Saved list Dialog */}
      <Dialog open={savedOpen} onOpenChange={setSavedOpen}>
        <DialogContent className="max-w-2xl" data-testid="cbam-scenario-saved-dialog">
          <DialogHeader>
            <DialogTitle>Saved scenarios</DialogTitle>
            <DialogDescription>Previous CBAM what-if analyses across your organisation.</DialogDescription>
          </DialogHeader>
          {savedLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
            </div>
          ) : savedList.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No saved scenarios yet. Run a simulation and click "Save scenario".
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {savedList.map((s) => (
                <div key={s.id} className="border rounded-lg p-3" data-testid={`cbam-scenario-saved-${s.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{s.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {s.product_name} · {new Date(s.created_at).toLocaleString()}
                      </div>
                      {s.description && <div className="text-xs mt-1 text-muted-foreground">{s.description}</div>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteSaved(s.id)}
                            data-testid={`cbam-scenario-saved-delete-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                    <div><span className="text-muted-foreground">Baseline </span>
                      <span className="font-mono">{fmtEur(s.baseline_impact)}</span></div>
                    <div><span className="text-muted-foreground">Scenario </span>
                      <span className="font-mono">{fmtEur(s.scenario_impact)}</span></div>
                    <div>
                      <span className="text-muted-foreground">Saving </span>
                      <span className={`font-mono font-semibold ${s.potential_saving >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {fmtEur(s.potential_saving)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
